// src/pages/ShopAccounts/ShopAccounts.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faExchangeAlt,
  faChartPie,
  faFileInvoiceDollar,
} from '@fortawesome/free-solid-svg-icons';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

// Define the Shop Accounts options
const shopAccountsOptions = [
  {
    title: 'Merchants Daily Report',
    icon: faDollarSign,
    path: '/shop-report/total-collection',
    modules: [3910],
    description: 'View daily merchant revenue and collection reports'
  },
  {
    title: 'Merchants Transaction Report',
    icon: faExchangeAlt,
    path: '/shop-report/transactions',
    modules: [3911, 3912],
    description: 'View complete merchant transaction and commission history'
  },
  {
    title: 'Merchants Advance Fund Report',
    icon: faFileInvoiceDollar,
    path: '/shop-report/advance-fund-report',
    modules: [3914, 3915],
    description: 'View merchant advance fund balances and transaction history'
  },
];

const ShopAccounts = () => {
  const [userLevel, setUserLevel] = useState(null);
  const [userModules, setUserModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // MODIFY hasAccess function to handle adminOnly cards
  const hasAccess = useCallback((modules, adminOnly = false) => {
    if (adminOnly) {
      const isAdmin = userLevel === 1 || userLevel === '1' || userLevel === 2 || userLevel === '2';
      return isAdmin;
    }
    if (userLevel === 1 || userLevel === '1' || userLevel === 2 || userLevel === '2') {
      return true;
    }
    const hasModuleAccess = modules.some(moduleId => userModules.includes(moduleId));
    return hasModuleAccess;
  }, [userLevel, userModules]);

  const fetchUserPrivileges = useCallback(async (userId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/privileges/modules/${userId}`, {
        timeout: 5000
      });

      if (response.status === 200 && response.data.modules) {
        setUserModules(response.data.modules);
      } else {
        setError('No modules found for user');
      }
    } catch (error) {
      setError('Failed to load user privileges');
      console.error('🔥 Error fetching user privileges:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      setError('Please login to continue');
      setIsLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setUserLevel(decodedToken.level);

      if (decodedToken.level === 1 || decodedToken.level === '1' || decodedToken.level === 2 || decodedToken.level === '2') {
        setUserModules(shopAccountsOptions.flatMap(option => option.modules));
        setIsLoading(false);
      } else {
        fetchUserPrivileges(decodedToken.userId);
      }
    } catch (error) {
      console.error('🔥 Error decoding token:', error);
      setError('Invalid authentication token');
      setIsLoading(false);
    }
  }, [fetchUserPrivileges]);

  const handleCardClick = useCallback((path) => {
    if (userLevel === 1 || userLevel === '1' || userLevel === 2 || userLevel === '2') {
      sessionStorage.setItem('isAdminUser', 'true');
    } else {
      sessionStorage.removeItem('isAdminUser');
    }
    navigate(path);
  }, [navigate, userLevel]);

  if (isLoading) {
    return (
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading shop accounts...
            </Typography>
          </Box>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
          <Typography variant="h6" color="error" sx={{ textAlign: 'center' }}>
            {error}
          </Typography>
        </Grid>
      </Box>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = shopAccountsOptions.some(option => hasAccess(option.modules, option.adminOnly));

  if (!hasAnyAccess) {
    return (
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
          <Typography variant="h6" color="error">
            You don't have permission to access shop account features. Please contact your administrator.
          </Typography>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 ,mt:-7}}>
        Merchant Reports
      </Typography>
      
    
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {shopAccountsOptions.map((option) => {
          const cardHasAccess = hasAccess(option.modules, option.adminOnly);
          
          return cardHasAccess && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 225,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      color="#FFA500"
                      style={{ marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ShopAccounts;